import { Box, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import { DatabaseFieldType } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FC, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import TagSelect from './TagSelect';

const valueContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const TagPopoverEdit: FC<EditComponentProps<DatabaseFieldType.SELECT>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  entryId,
  addChangeToDraft,
  contentId,
  placeholder,
  appearance,
  isRenderedInSidePanel,
  isFilterValueEdit,
  isBulkEdit,
}) => {
  // This resolves a specific issue where an empty tag cell occasionally shows "no tag" when you first open it.
  // For some reason the value of that tag field is set to  an empty string.
  const hasCurrentData = value.length > 0 && value[0] !== '';
  const isMulti = field.settings.multi || value.length > 1;
  const { formatMessage } = useIntl();
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);
  const currentDataSectionRef = useRef<HTMLDivElement | null>(null);
  const canCreate = defaultStoreUtils.useSelector((state) => {
    if (!contentId) {
      return false;
    }
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.operations.updateMetadata && !database.meta.isLocked;
  });

  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });

  const handleRemove = useCallback(
    (removedValues: string[], allSelectedValues: string[]) => {
      const currentlySelectedValues = allSelectedValues.filter((value: string) => {
        return !removedValues.includes(value);
      });

      onChange(currentlySelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: value.indexOf(removedValues[0]), // FIXME: index?
        },
      });

      // If we are removing the last value, focus the select input
      if (value.length === 1) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onChange, value]
  );

  const extractOptionDataFromFields = useCallback(
    (option: string) => {
      const fieldData = field.settings.selectOptions.find((o) => o.id === option);
      return {
        label:
          fieldData?.label ?? formatMessage(messages['board-layout.empty-tag-label']),
        id: fieldData?.id,
        color: fieldData?.color ?? 'greyLight',
      };
    },
    [field, formatMessage]
  );

  const TagSelectAddDataSection = useMemo(
    () => (
      <div ref={addDataSectionRef}>
        <TagSelect
          options={field.settings.selectOptions}
          value={value}
          isCreatable={isFilterValueEdit ? false : canCreate}
          isMulti={isMulti}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          placeholder={placeholder}
          preventOpenWhenNoOptions={false}
          appearance={appearance}
          addChangeToDraft={addChangeToDraft}
          onChange={onChange}
          field={field}
          isRenderedInSidePanel={isRenderedInSidePanel}
          addDataSectionRef={addDataSectionRef}
        />
      </div>
    ),
    [
      value,
      isFilterValueEdit,
      canCreate,
      isMulti,
      onFocus,
      onBlur,
      autoFocus,
      placeholder,
      appearance,
      addChangeToDraft,
      onChange,
      field,
      isRenderedInSidePanel,
    ]
  );

  const TagSelectCurrentDataSection = useMemo(
    () => (
      <Box xcss={valueContainerStyles} ref={currentDataSectionRef}>
        {value.map((option) => (
          <SelectOptionTag
            key={option}
            option={extractOptionDataFromFields(option)}
            isRemovable
            onRemove={() => handleRemove([option], value)}
          />
        ))}
      </Box>
    ),
    [value, extractOptionDataFromFields, handleRemove]
  );

  return (
    <PopoverEdit
      isMultiSelect={field.settings.multi}
      hasCurrentData={hasCurrentData}
      currentDataSection={TagSelectCurrentDataSection}
      addDataSection={TagSelectAddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default TagPopoverEdit;
